import { Link, useLocation } from "react-router-dom";
import BountyBadge from "@/components/BountyBadge";
import { MapPin, Briefcase, Lock, AlertTriangle } from "lucide-react";

export default function JobCard({ job }) {
    const { search } = useLocation();
    const formatSalary = (a) => `$${(a / 1000).toFixed(0)}k`;
    const bountyAmount = job.bounty || job.referral_bounty || 0;
    const isIntel = !!job.intel_category;
    const isLocked = !!job.is_redacted;
    return (
        <Link
            to={{ pathname: `/jobs/${job.id}`, search }}
            data-testid={`job-card-${job.id}`}
            data-locked={isLocked ? "true" : "false"}
            className={`group relative flex flex-col gap-5 overflow-hidden rounded-md border p-7 backdrop-blur-xl transition-all duration-500 ${
                isLocked
                    ? "border-[#C9A96E]/20 bg-[#141414]/40 hover:border-[#C9A96E]/40"
                    : "border-white/5 bg-[#141414]/60 hover:border-[#C9A96E]/30 hover:bg-[#1F1F1F]/80 hover:shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
            }`}
        >
            {isLocked && (
                <div
                    data-testid={`job-card-locked-${job.id}`}
                    className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-wider"
                    style={{
                        background: "rgba(201,169,110,0.1)",
                        color: "var(--brand-accent)",
                        border: "1px solid rgba(201,169,110,0.3)",
                    }}
                >
                    <Lock size={10} /> LOCKED
                </div>
            )}
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <div className="caption text-[#737373]">
                        {job.company}
                        {job.is_external && <span className="ml-2 text-[var(--brand-accent)]">· EXTERNAL</span>}
                        {isIntel && (
                            <span className="ml-2 text-[var(--brand-accent)]">
                                · {job.intel_category === "security_strike" ? "SECURITY STRIKE" : "CONTROL AUDIT"}
                            </span>
                        )}
                        {job.mission_type && <span className="ml-2 text-[#A3A3A3]">· {job.mission_type.toUpperCase()}</span>}
                    </div>
                    <h3 className={`mt-1 font-serif text-2xl leading-tight tracking-display ${isLocked ? "text-[#A3A3A3] italic" : "text-[#FAFAFA]"}`}>
                        {job.title}
                    </h3>
                </div>
                {bountyAmount > 0 && <BountyBadge amount={bountyAmount} size="sm" testId={`job-card-bounty-${job.id}`} />}
            </div>

            {isIntel && typeof job.risk_score === "number" && (
                <div className="inline-flex items-center gap-2 text-xs">
                    <AlertTriangle size={12} className="text-[var(--brand-accent)]" />
                    <span className="font-mono uppercase tracking-wider text-[#A3A3A3]">
                        Risk Score
                    </span>
                    <span className="font-mono text-[var(--brand-accent)]">{job.risk_score}/100</span>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#A3A3A3]">
                <span className="inline-flex items-center gap-2">
                    <MapPin size={14} className="opacity-60" />
                    {job.location}
                </span>
                <span className="inline-flex items-center gap-2">
                    <Briefcase size={14} className="opacity-60" />
                    {job.seniority}
                </span>
                {!isIntel && (
                    <span className="font-mono text-xs text-[#737373]">
                        {formatSalary(job.salary_min)} – {formatSalary(job.salary_max)}
                    </span>
                )}
            </div>

            {isLocked ? (
                <div className="rounded-sm border border-dashed border-white/10 bg-white/[0.02] px-3 py-3 text-xs text-[#737373]">
                    {job.intel_summary || "Sign in & complete Mission Intake to unlock the dossier."}
                </div>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {(job.skills || []).slice(0, 4).map((s) => (
                        <span
                            key={s}
                            className="rounded-sm border border-white/5 bg-white/[0.02] px-2.5 py-1 text-xs text-[#A3A3A3]"
                        >
                            {s}
                        </span>
                    ))}
                    {job.skills && job.skills.length > 4 && (
                        <span className="px-2.5 py-1 text-xs text-[#737373]">+{job.skills.length - 4} more</span>
                    )}
                </div>
            )}

            <div className="mt-1 flex items-center justify-between border-t border-white/5 pt-4">
                <span className="caption text-[#737373]">{job.region}</span>
                <span
                    className="caption transition-all group-hover:text-[var(--brand-accent)]"
                    style={{ color: "#A3A3A3" }}
                >
                    {isLocked ? "Unlock dossier →" : "View brief →"}
                </span>
            </div>
        </Link>
    );
}
